import React, { Suspense, useEffect, useState, useRef } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";
import firebase from "firebase/app";
import "firebase/messaging";

import { initFirebase } from "./firebaseInit";
import SignUpContainer from "./containers/SignUpContainer";
import DoctorSignUpContainer from "./containers/DoctorSignUpContainer";
import LoginContainer from "./containers/LoginContainer";
import Dashboard from "./containers/Dashboard";
import TestComponent from "./components/boxes/TestComponent";
import "./App.css";
import "./index.css";
import ResetPasswordContainer from "./containers/ResetPasswordContainer";
import PlanExpiredPage from "./components/PlanExpiredPage";
import PaymentSuccessfulPage from "./components/PaymentSuccessfulPage";
import LogoutContainer from "./containers/LogoutContainer";
import { withUserOIDC } from "./components/utils/OIDCHOC";
import LanguageSwitcher from "./components/languageSwitcher/LanguageSwitcher";
import { PulseLoader } from "react-spinners";
import { useFeatureFlags } from "./components/utils/FeatureFlagWrapper";
import ReferContainer from "./containers/ReferContainer";

import { AuthProvider } from "./components/utils/oidc/AuthProvider";
import { useAuth } from "./components/utils/oidc/AuthProvider";
import Callback from "./components/utils/oidc/Callback";
import OidcLoginPage from "./components/utils/oidc/OidcLoginPage";
import Localization from "./utils/localization";
import AppOld from "./AppOld";
import { envVariables } from "./config/appConfig";
import LoadingScreen from "./components/LoadingScreen";
import userService from "./services/userService";
import { getSubscription } from "./services/careBalanceService";
import { shouldRouteExpiredPlanUsers } from "./components/care-balance/utils/should-route-to-expiry-page";
import { forceLogout } from "./services/oidc/authConfig";

// 🔥 Initialize Firebase BEFORE React renders
if (!process.env.DISABLE_FIREBASE) {
  initFirebase();
}

const PUBLIC_PATHS = [
  "/login",
  "/register",
  "/logout",
  "/callback",
  "/reset-password",
  "/plan-expired",
  "/payment-successful",
  "/test",
  "/doctor-signup",
];

// Runs inside AuthProvider + Router — checks profile/plan before rendering protected routes
const AppRoutes = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const isPublicPath = PUBLIC_PATHS.includes(window.location.pathname);
  const isPersisted = localStorage.getItem("isAuthenticated") === "true";

  // Block the Switch from rendering at all until the status check completes.
  // Initialize to true for sessions that appear authenticated on a protected path
  // so the Dashboard never flashes before we know the user's plan status.
  const [statusLoading, setStatusLoading] = useState(
    isPersisted && !isPublicPath,
  );
  const [isInactiveUser, setIsInactiveUser] = useState(false);
  const hasChecked = useRef(false);

  useEffect(() => {
    // Auth finished loading and user is not authenticated — release the loading screen
    // so the router can redirect to /login naturally.
    if (!isLoading && !isAuthenticated) {
      setStatusLoading(false);
      return;
    }

    if (!isAuthenticated || isLoading || hasChecked.current || isPublicPath)
      return;

    hasChecked.current = true;
    setStatusLoading(true);

    const checkUserStatus = async () => {
      try {
        const [profileResult, planResult] = await Promise.allSettled([
          userService.getProfile(),
          getSubscription(),
        ]);

        const profile =
          profileResult.status === "fulfilled" ? profileResult.value : null;
        if (
          profile?.status === "error" &&
          profile?.message === "Inactive profile"
        ) {
          setIsInactiveUser(true);
          return;
        }

        if (profileResult.status === "rejected") throw profileResult.reason;
        if (planResult.status === "rejected") throw planResult.reason;

        if (shouldRouteExpiredPlanUsers({ planInfo: planResult.value })) {
          setIsInactiveUser(true);
        }
      } catch (err) {
        if (err?.status === 401 || err?.response?.status === 401) {
          forceLogout("session_invalid");
          return;
        }
        if (
          err?.message === "Inactive profile" ||
          err?.errorMessage === "Inactive profile"
        ) {
          setIsInactiveUser(true);
        }
      } finally {
        setStatusLoading(false);
      }
    };

    checkUserStatus();
  }, [isAuthenticated, isLoading, isPublicPath]);

  if (statusLoading) return <LoadingScreen />;

  return (
    <Switch>
      <Route component={OidcLoginPage} path="/login" exact />
      <Route component={SignUpContainer} path="/register" exact />
      <Route component={Callback} path="/callback" exact />
      <Route component={DoctorSignUpContainer} path="/doctor-signup" exact />
      <Route component={ResetPasswordContainer} path="/reset-password" exact />
      <Route component={TestComponent} path="/test" exact />
      <Route component={PlanExpiredPage} path="/plan-expired" exact />
      <Route
        component={PaymentSuccessfulPage}
        path="/payment-successful"
        exact
      />
      <Route component={LogoutContainer} path="/logout" exact />
      {isInactiveUser ? (
        <Redirect to="/plan-expired" />
      ) : (
        <>
          <Route component={withUserOIDC(ReferContainer)} path="/refer" />
          <Route component={withUserOIDC(Dashboard)} path="/" />
        </>
      )}
    </Switch>
  );
};

const App = () => {
  const { flags } = useFeatureFlags();

  // 🔹 Service worker toggle
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.active?.postMessage({
          type: "TOGGLE_NOTIFICATIONS",
          enabled: JSON.parse(
            localStorage.getItem("notificationsEnabled") || "true",
          ),
        });
      });
    }
  }, []);

  // 🔹 Safe FCM foreground listener (iOS protected)
  useEffect(() => {
    let unsubscribe;

    const setupMessaging = async () => {
      if (!firebase.apps.length) return;

      const supported = await firebase.messaging.isSupported();
      if (!supported) {
        console.log("FCM not supported on this device/browser");
        return;
      }

      const messaging = firebase.messaging();

      unsubscribe = messaging.onMessage((payload) => {
        try {
          const parsed = JSON.parse(payload.data.data);

          const isCategoryPicked = parsed.category === 6;
          const isTypePicked = parsed.type === 2;
          const messageId = parsed.message.id;

          const isConsultationPathMatch =
            window.location.pathname === `/consultations/${messageId}/messages`;

          if (isCategoryPicked && isTypePicked && isConsultationPathMatch) {
            window.location.reload();
          } else {
            console.info("FCM received but not on target page");
          }
        } catch (err) {
          console.error("Failed to parse FCM payload", err);
        }
      });
    };

    setupMessaging();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  return (
    <Suspense
      fallback={
        <div className="loader-fb">
          <PulseLoader color="#094063" size={7} />
        </div>
      }
    >
      <AuthProvider>
        <Router>
          <div className="content dash">
            {Localization.isLanguageOptionOptionEnabled() && (
              <LanguageSwitcher />
            )}
            <AppRoutes />
          </div>
        </Router>
      </AuthProvider>
    </Suspense>
  );
};

export default envVariables.REACT_APP_FEATURE_ENABLE_KEYCLOAK_SSO
  ? App
  : AppOld;
////


